
import { HttpClient, HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SKIP_SERVER_ERROR_INTERCEPTOR } from '@core/interceptors/server-error.interceptor';
import { parseToProcessDto, parseToProcessNotification, Process, PROCESS_STATUS, ProcessDto, ProcessNotification } from '@core/types/process.types';
import { downloadFile } from '@shared/functions/download-file';
import { BehaviorSubject, catchError, distinctUntilChanged, map, MonoTypeOperatorFunction, Observable, of, scan, shareReplay, switchMap, tap, throttleTime, timer, withLatestFrom } from 'rxjs';
import { ToastService } from '../toast/toast.service';
import { ProcessToast } from '@core/configs/toast/toast.types';
import { intersectionBy } from 'es-toolkit';


function getProcessFileName (process: Process): string {
  return `${ process.name }-${ process.contextName }.pdf`.toLowerCase();
}


@Injectable({
  providedIn: 'root'
})
export class ProcessService {
  private readonly _httpClient: HttpClient = inject(HttpClient);

  private readonly _toastService: ToastService = inject(ToastService);

  private readonly _pollingIntervallInMilliseconds: number = 10_000;

  private readonly _baseEndpoint: string = "api/forms";

  private readonly _forceImmediatePoll$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);

  /**
   * Ids of Processes that were created and are waiting for the next poll to be shown as a toast.
   */
  private readonly _pendingProcessIds$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);


  /**
   * Observable that emits every time when a poll should happen
   */
  private readonly _poll$: Observable<void> = this._forceImmediatePoll$
    .pipe(
      throttleTime(1000, undefined, { trailing: true, leading: true }),
      switchMap(() => timer(0, this._pollingIntervallInMilliseconds)
        .pipe(
          map(() => void 0)
        ))
    );

  public readonly processNotifications$: Observable<ProcessNotification[]> = this._poll$
    .pipe(
      switchMap(() =>
        this.fetchProcessNotifications().pipe(
          catchError(() => of<ProcessNotification[]>([]))
        )),
      // Get PendingProcessIds an look in Poll if new Process was started
      withLatestFrom(this._pendingProcessIds$),
      tap(([ processNotifications, pendingProcessIds ]: [ProcessNotification[], string[]]) => {
        if (processNotifications.length === 0 || pendingProcessIds.length === 0) return;

        const recentlyStartedProcesses: Process[] = processNotifications
          .filter((notification: ProcessNotification) => pendingProcessIds.includes(notification.process.id)
            && notification.process.status !== PROCESS_STATUS.readyToDownload
            && notification.process.status !== PROCESS_STATUS.failed)
          .map((notification: ProcessNotification) => notification.process);

        this.showToastsForProcesses(recentlyStartedProcesses);

        // Remove all ids that were matched and shown as a Toast
        const nextPending: string[] = pendingProcessIds
          .filter((id: string) => !recentlyStartedProcesses.some((process: Process) => process.id === id));

        this._pendingProcessIds$.next(nextPending);
      }),
      map(([ processNotifications, _ ]: [ProcessNotification[], string[]]) => processNotifications),
      // Scan for processes that just finished to show toast
      scan((previousNotifications: ProcessNotification[], currentNotifications: ProcessNotification[]) => {
        const processesFinishedInCurrentPoll: Process[] = intersectionBy(
          currentNotifications.filter((notification: ProcessNotification) => notification.process.status === PROCESS_STATUS.readyToDownload),
          previousNotifications.filter((notification: ProcessNotification) =>
            notification.process.status !== PROCESS_STATUS.failed
            && notification.process.status !== PROCESS_STATUS.readyToDownload),
          (notification: ProcessNotification) => notification.process.id
        ).map((notification: ProcessNotification) => notification.process);

        this.showToastsForProcesses(processesFinishedInCurrentPoll);

        return currentNotifications;
      }, [] as ProcessNotification[])
      ,
      shareReplay({ bufferSize: 1, refCount: true })
    );


  public hasRunningProcesses$: Observable<boolean> = this.processNotifications$.pipe(
    map((processes: ProcessNotification[]) => processes.some((notification: ProcessNotification) => notification.process.status === PROCESS_STATUS.running
      || notification.process.status === PROCESS_STATUS.waitingInQueue
      || notification.process.status === PROCESS_STATUS.evaluationComplete)),
    distinctUntilChanged()
  );

  public amountOfUnreadProcesses$: Observable<number> = this.processNotifications$.pipe(
    map((processes: ProcessNotification[]) => processes.filter((notification: ProcessNotification) => !notification.isSeen && notification.process.status === PROCESS_STATUS.readyToDownload).length),
    distinctUntilChanged()
  );

  /**
   * Registers a given processId to be shown as a Toast the next Time the service polls and matches the return to the id.
   *
   * @param processId - The Id of the process
   */
  public registerProcessIdForToast (processId: string): void {
    this._pendingProcessIds$.next([ ...this._pendingProcessIds$.getValue(), processId ]);
    this._forceImmediatePoll$.next();
  }


  private showToastsForProcesses (processes: Process[]): void {
    for (const process of processes) {
      this._toastService.show({ kind: "process",
        payload: { process } satisfies ProcessToast });
    }
  }

  private refreshOnSuccess<T> (): MonoTypeOperatorFunction<T> {
    return tap({ finalize: () => this._forceImmediatePoll$.next() });
  }


  private fetchProcessNotifications (): Observable<ProcessNotification[]> {
    const httpContext: HttpContext = new HttpContext().set(SKIP_SERVER_ERROR_INTERCEPTOR, true);

    return this._httpClient.get<ProcessDto[]>(`${ this._baseEndpoint }/print-center`, { context: httpContext }).pipe(
      map((processDto: ProcessDto[]) => processDto.map(parseToProcessNotification))
    );
  }

  private updateProcess (update: Partial<ProcessDto>): Observable<HttpResponse<void>> {
    return this._httpClient.put<undefined>(`${ this._baseEndpoint }/print-center/${ update.printId }`, update, { observe: "response" }).pipe(this.refreshOnSuccess());
  }

  /**
   * Delete a Process
   *
   * @param processId - The identifier of the Process
   *
   * @return an http-response-observable of the deletion
   * @returns
   */
  public deleteProcess (processId: string): Observable<HttpResponse<void>> {
    return this._httpClient[ "delete" ]<HttpResponse<void>>(`${ this._baseEndpoint }/print-center/${ processId }`).pipe(this.refreshOnSuccess());
  }

  /**
   * Pause a Process that is waiting in the Queue
   *
   * @param processId - The identifier of the Process
   *
   * @return an http-response-observable of the deletion
   * @returns
   */
  public pauseProcess (processId: string): Observable<HttpResponse<void>> {
    const pause: Partial<ProcessDto> = parseToProcessDto({ id: processId, status: PROCESS_STATUS.paused });

    return this.updateProcess(pause);
  }

  /**
   * Restart a Process that failed/has been paused
   *
   * @param processId - The identifier of the Process
   *
   * @return an http-response-observable of the deletion
   * @returns
   */
  public restartProcess (processId: string): Observable<HttpResponse<void>> {
    const restart: Partial<ProcessDto> = { printId: processId, restart: true };

    return this.updateProcess(restart);
  }

  /**
   * Read/Unread a Process that was completed.
   *
   * @param processId - The identifier of the Process
   * @param isSeen - whether the process isSeen or not
   *
   * @return an http-response-observable of the mark as seen
   */

  public isProcessSeen (processId: string, isSeen: boolean): Observable<HttpResponse<void>> {
    const restart: Partial<ProcessDto> = { printId: processId, markedAsSeen: isSeen };

    return this.updateProcess(restart);
  }

  /**
   * Mark all completed unseen Processes as seen
   *
   * @return an http-response-observable of the mark all as seen
   * @returns
   */
  public markAllProcessesSeen (): Observable<HttpResponse<void>> {
    return this._httpClient.put<HttpResponse<void>>(`${ this._baseEndpoint }/print-center/seen-all`, {}).pipe(this.refreshOnSuccess());
  }

  /**
   * Download the processed PDF
   *
   * @param process - The process to download. It's also needed to define the filename.
   *
   * @return an http-response-observable of the download
   * @returns
   */
  public downloadProcess (process: Process): Observable<HttpResponse<Blob>> {
    const params: HttpParams = new HttpParams().set("isPdf", true);

    return this._httpClient.get(`${ this._baseEndpoint }/print/${ process.id }/download`, {
      params,
      responseType: "blob",
      observe: "response"
    }).pipe(
      this.refreshOnSuccess(),
      tap((res: HttpResponse<Blob>) => {
        if (res.body) {
          const blob: Blob = new Blob([ res.body ], { type: 'application/pdf' });
          const fileURL: string = URL.createObjectURL(blob);

          downloadFile(fileURL, getProcessFileName(process));
        }
      })
    );
  }
}
